/**
 * Teacher → Students lookup
 *
 * Resolves the logged-in teacher's students by walking the class linkage:
 *   auth.uid → users.id → teachers.user_id → teachers.id
 *   → class_teachers.teacher_id → class_id → class_students.student_id
 *
 * The Yudu data model uses two junction tables: `class_teachers` ties a
 * teacher to one or more classes, and `class_students` ties students to
 * those classes. A teacher's "my students" set is the union of student_ids
 * across every class they teach.
 *
 * Why this hook exists: TeacherStudents.tsx and a couple other teacher
 * surfaces were calling the generic `useGetStudentsApi({})` from
 * services/shared/student which returns every student in the org (RLS is
 * org-scoped only). Phase A of the role-scoping fix routes those surfaces
 * through this hook instead; Phase B will tighten RLS so direct REST
 * calls also respect the class linkage.
 */
import { useCallback, useEffect, useState } from "react";
import { supabase } from "../supabase";
import { useAuth } from "../../context/AuthContext";
import { getErrorMessage } from "@utils/helper";
import useLookupStore from "@setup/store/useLookupStore";
import type { SchoolYearModel } from "@components/models/school-year/SchoolYearModel";
import type { StudentMetricsRow } from "../shared/studentMetrics";
import type { GradeSummaryRow } from "../shared/grades";

// Lazy import to avoid a circular dependency with api.ts (same pattern used by
// services/shared/studentMetrics.ts and services/shared/grades.ts).
const getApi = () => import("../api").then((m) => m.api);

export interface TeacherStudentLookup {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

export function useGetMyTeacherStudentsLookup() {
  const { profile } = useAuth();
  const [rows, setRows] = useState<TeacherStudentLookup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStudents = useCallback(async () => {
    if (!profile?.id) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // 1. Resolve teachers.id from the auth profile (a `users` row).
      const { data: teacher, error: tErr } = await supabase
        .from("teachers")
        .select("id")
        .eq("user_id", profile.id)
        .maybeSingle();
      if (tErr) throw tErr;
      if (!teacher) {
        setRows([]);
        return;
      }

      // 2. Class ids this teacher teaches.
      const { data: classRows, error: cErr } = await supabase
        .from("class_teachers")
        .select("class_id")
        .eq("teacher_id", (teacher as { id: string }).id);
      if (cErr) throw cErr;
      const classIds = (classRows ?? [])
        .map((r) => r.class_id)
        .filter(Boolean);
      if (classIds.length === 0) {
        setRows([]);
        return;
      }

      // 3. Students enrolled in any of those classes. Embed the student
      // record so the caller gets first_name/last_name/email in one round
      // trip — no follow-up by-id query.
      const { data: enroll, error: eErr } = await supabase
        .from("class_students")
        .select("students(id, first_name, last_name, email)")
        .in("class_id", classIds);
      if (eErr) throw eErr;

      // De-dupe by student id — a student in two classes the same teacher
      // teaches would otherwise appear twice in the dropdown.
      const seen = new Set<string>();
      const mapped: TeacherStudentLookup[] = [];
      for (const row of enroll ?? []) {
        // `students` is a to-one embed; PostgREST may type it as an array.
        const s = Array.isArray(row.students) ? row.students[0] : row.students;
        if (!s || seen.has(s.id)) continue;
        seen.add(s.id);
        mapped.push({
          id: s.id,
          first_name: s.first_name ?? null,
          last_name: s.last_name ?? null,
          email: s.email ?? null,
        });
      }
      setRows(mapped);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load students'));
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  return { rows, loading, error, refetch: fetchStudents };
}

// ---------------------------------------------------------------------------
// Teacher roster WITH metrics — backs the dashboard Progress + Performance
// widgets.
//
// `useGetMyTeacherStudentsLookup` above returns names only. The dashboard fed
// those rows straight into the Progress / Performance tables, which read
// `attributes.progress`, `attributes.semsester_avg` and `attributes.gpa` — keys
// that never existed on a lookup row, so both widgets rendered permanently
// blank. That was fixed in #565 by running the shared `computeChildMetrics`
// engine client-side (the SAME engine services/parent/students.ts used, #505).
//
// UAT 8/10 (PR-6) — "child progress 0%": computeChildMetrics' progress
// NUMERATOR reads `student_grades` / `activity_answers` directly, under the
// TEACHER's own JWT. Teacher RLS on those tables is activity-keyed
// (activity -> course -> class_id), and that class_id is NULL or wrong-class
// for most rows, while the DENOMINATOR (course_enrollments -> activities) is
// org-readable and unaffected — numerator 0 / denominator populated = a
// hardcoded 0% for almost every teacher-viewed student, however much work
// they'd actually done. Broadening the RLS policy was considered and
// REJECTED (widens what a teacher can read well beyond what the roster
// needs). The fix instead REPOINTS to `GET /api/grading/summary`
// (gradingReads.ts), which already computes progress/gpa/status/last_access
// SERVER-side with the service-role client (bypasses RLS entirely) and
// narrows to the teacher's own students via `resolveAllowedStudentIds` — no
// per-row class linkage to get wrong.
//
// MERGE RULE (do not simplify away): /summary keys off `student_grade_levels`,
// so it can only answer for a student with an SGL row matching its filters
// (`active: true` here, mirroring the parent-teacher roster's proven query in
// parentTeacherWorkSpace/students/Students.tsx). On staging only 48 of 53
// students across a teacher's classes have an active SGL row — the other 5
// are simply MISSING from the summary response. The roster list itself (WHO
// appears, names, email, user_name) always comes from the class_teachers ->
// class_students query below, never from /summary — the summary response is
// only ever consulted as a metrics lookup, LEFT-JOINed onto that roster by
// student id (fetchTeacherGradingSummary / toRosterMetrics below). A student
// /summary has no row for — whatever the reason, an inactive SGL row or
// otherwise — gets all-null metrics and renders "—", but is NEVER dropped
// from the table. Same graceful-miss shape if the /summary call itself fails
// (network, 500): every roster student still renders, just with null metrics
// instead of the whole roster vanishing.
//
// /summary has no class_id filter, so it is fetched UNSCOPED (the teacher's
// whole allowed set) on every call; class scoping is preserved entirely by
// the roster id list this map gets left-joined onto (already narrowed to
// `classId` below), not by the summary fetch itself.
//
// One side effect: the teacher and parent rosters now source their numbers
// from two different engines (this file vs. computeChildMetrics) — the #565
// comment's "same shared engine" promise no longer holds for teacher vs.
// parent. That is intentional: the parent path's RLS was never activity-keyed
// like the teacher's (a parent's RLS scopes to their own children), so its
// numerator was never broken and it stays on the client-side engine — see
// studentMetrics.ts for that engine's own PR-6 fixes (dedupe + withdrawn
// filter), which still apply there.
// ---------------------------------------------------------------------------

export type TeacherStudentRow = StudentMetricsRow;

/** The /summary-derived fields this roster merges onto the class roster. */
interface TeacherRosterMetrics {
  progressCompleted: number | null;
  semesterAvg: number | null;
  gpa: string | number | null;
  lastAccess: string | null;
  status: string | null;
}

const NULL_ROSTER_METRICS: TeacherRosterMetrics = {
  progressCompleted: null,
  semesterAvg: null,
  gpa: null,
  lastAccess: null,
  status: null,
};

/**
 * Shape one /api/grading/summary row into this roster's metrics fields, or
 * return all-null when the roster student has no matching row (LEFT-JOIN
 * miss — see the MERGE RULE above).
 *
 * `progress` / `gpa` / `last_access` / `status` are read straight off the row
 * — the four fields /summary computes server-side. There is no single
 * "current period average" field on a /summary row: `grades` is EVERY period
 * the student has a grade in, ascending by period number (not filtered to
 * "current"). We take the LAST (highest-ranked, i.e. most recently dated)
 * entry as the `semsester_avg` proxy. This is an approximation, not the same
 * active-school-year / contains-today / latest disambiguation ladder
 * computeChildMetrics runs — flagged as a follow-up candidate in the PR
 * description if an exact "current period" signal is ever needed here.
 */
function toRosterMetrics(row: GradeSummaryRow | undefined): TeacherRosterMetrics {
  if (!row) return NULL_ROSTER_METRICS;
  const grades = row.grades ?? [];
  const latestPeriod = grades.length > 0 ? grades[grades.length - 1] : undefined;
  return {
    progressCompleted: typeof row.progress === "number" ? row.progress : null,
    semesterAvg:
      latestPeriod && typeof latestPeriod.grade === "number"
        ? Math.round(latestPeriod.grade)
        : null,
    gpa: typeof row.gpa === "number" ? row.gpa.toFixed(2) : null,
    lastAccess: row.last_access ?? null,
    status: row.status ?? null,
  };
}

/**
 * Fetch this teacher's ENTIRE /api/grading/summary roster (server narrows to
 * the caller via resolveAllowedStudentIds — there is no class_id param to
 * pass), paging past the endpoint's 25-row page size, keyed by student id.
 *
 * `active: true` mirrors the parent-teacher roster's existing, proven query
 * (parentTeacherWorkSpace/students/Students.tsx) — the same filter that
 * produces the "48 of 53" SGL gap the MERGE RULE above handles.
 *
 * `schoolYearId` (UAT 8/10, PR-6 follow-up): /summary's student_grade_levels
 * query only filters by school year when this param is given
 * (gradingReads.ts ~176: `if (q.school_year_id) sglQuery = sglQuery.eq(...)`)
 * — omitted, it returns the RAW (non-deduped) enrollment rows across EVERY
 * school year the org has. A student with more than one `active: true` SGL
 * row (re-enrolled, or repeating a grade across years) then gets ONE roster
 * row PER school year in the response (gradingReads.ts builds `rows` straight
 * off `enrollments`, not the deduped-by-student map it also keeps), all
 * pooled under the same student-id-keyed grade/GPA aggregates — i.e. literal
 * duplicate rows for that student, non-deterministic ordering (Postgres does
 * not guarantee which school year's enrollment row survives the "first one
 * wins" dedupe used elsewhere in that handler), and grades from different
 * school years colliding on the same period number ("semester avg" could
 * silently be a prior year's Q4). Passing school_year_id also activates that
 * handler's grading_periods lookup, so period names resolve instead of
 * falling back to generic "Period N" labels. Optional — omitted, the request
 * behaves exactly as before (see toRosterMetrics' merge-rule comment).
 *
 * Swallows its own errors (logs + returns an empty map) instead of throwing:
 * a metrics-endpoint hiccup should degrade to an all-null-metrics roster, not
 * blank the whole page the way a thrown error inside fetchStudents would.
 */
async function fetchTeacherGradingSummary(
  schoolYearId?: string,
): Promise<Map<string, GradeSummaryRow>> {
  const bySid = new Map<string, GradeSummaryRow>();
  try {
    const api = await getApi();
    let page = 1;
    let fetched = 0;
    // Safety backstop (mirrors fetchAllRows.ts's MAX_PAGES): never loop
    // forever if total_count is ever wrong. 40 pages x 25 rows = 1,000
    // students — far above any real teacher's roster.
    const MAX_PAGES = 40;
    while (page <= MAX_PAGES) {
      const res = await api.get("/api/grading/summary", {
        params: { active: true, page, school_year_id: schoolYearId },
      });
      const rows = (res?.data?.student_gradebook ?? []) as GradeSummaryRow[];
      for (const row of rows) {
        const sid = row?.student?.id;
        if (sid) bySid.set(sid, row);
      }
      fetched += rows.length;
      const totalCount = res?.data?.total_count ?? 0;
      if (rows.length === 0 || fetched >= totalCount) break;
      page += 1;
    }
  } catch (err) {
    console.error(
      "[useGetTeacherStudents] /api/grading/summary failed; roster metrics will render '—'",
      err,
    );
  }
  return bySid;
}

/**
 * The logged-in teacher's students, with real metrics, in the DataTable
 * `attributes.*` envelope.
 *
 * @param classId  optional — when set, scope the roster to that ONE class
 *                 instead of the union of every class the teacher teaches.
 *                 This is the dashboard's class picker. The id is intersected
 *                 with the teacher's own `class_teachers` rows, so passing a
 *                 class they do not teach yields an empty roster rather than
 *                 another role's students (the picker is fed from the org-wide
 *                 lookup store).
 */
export function useGetTeacherStudents(classId?: string) {
  const { profile } = useAuth();
  const [rows, setRows] = useState<TeacherStudentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStudents = useCallback(async () => {
    if (!profile?.id) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // 1. teachers.id from the auth profile (a `users` row).
      const { data: teacher, error: tErr } = await supabase
        .from("teachers")
        .select("id")
        .eq("user_id", profile.id)
        .maybeSingle();
      if (tErr) throw tErr;
      if (!teacher) {
        setRows([]);
        return;
      }

      // 2. Class ids this teacher teaches, narrowed to the selected class.
      const { data: classRows, error: cErr } = await supabase
        .from("class_teachers")
        .select("class_id, classes(school_year_id)")
        .eq("teacher_id", (teacher as { id: string }).id);
      if (cErr) throw cErr;
      const taughtClassIds = (classRows ?? []).map((r) => r.class_id).filter(Boolean);
      const classIds = classId
        ? taughtClassIds.filter((id) => id === classId)
        : taughtClassIds;

      const placementSchoolYearIds = [
        ...new Set(
          (classRows ?? [])
            .filter((r) => classIds.includes(r.class_id))
            .map((r) => (Array.isArray(r.classes) ? r.classes[0] : r.classes)?.school_year_id)
            .filter((id): id is string => Boolean(id)),
        ),
      ];
      if (classIds.length === 0) {
        setRows([]);
        return;
      }

      // 3. Students in those classes, with the student record embedded.
      const { data: enroll, error: eErr } = await supabase
        .from("class_students")
        .select("students(id, first_name, last_name, email, user_name)")
        .in("class_id", classIds);
      if (eErr) throw eErr;

      // De-dupe: a student in two of this teacher's classes appears twice.
      const seen = new Set<string>();
      const students: Array<{
        id: string;
        first_name: string | null;
        last_name: string | null;
        email: string | null;
        user_name: string | null;
      }> = [];
      for (const row of enroll ?? []) {
        // `students` is a to-one embed; PostgREST may type it as an array.
        const s = Array.isArray(row.students) ? row.students[0] : row.students;
        if (!s || seen.has(s.id)) continue;
        seen.add(s.id);
        students.push({
          id: s.id,
          first_name: s.first_name ?? null,
          last_name: s.last_name ?? null,
          email: s.email ?? null,
          user_name: (s as { user_name?: string | null }).user_name ?? null,
        });
      }

      // 4. Metrics: LEFT-join /api/grading/summary onto the roster above by
      // student id (see the MERGE RULE comment above fetchTeacherGradingSummary
      // / toRosterMetrics). Skipped entirely for an empty roster — same
      // short-circuit computeChildMetrics used to apply.
      //
      // Resolve the org's current school year to scope that fetch (see the
      // `schoolYearId` param comment on fetchTeacherGradingSummary for why
      // this matters for multi-year students). This hook has no page-level
      // filter of its own to read a school year from (unlike the registrar /
      // PT gradebook rosters, which take it from the URL's `school_year_id`
      // search param), so it resolves it the same way the admin + PT
      // CourseDialogs already do —
      // `schoolYears.find(sy => sy.status === 'active') ?? schoolYears[0]` —
      // reading the lookup store's vanilla state directly, since this is a
      // plain async function, not a component/hook, and can't call the
      // `useLookupStore()` hook form. Same non-reactive getState() access
      // reportError.ts already uses for useNotificationStore.
      const schoolYears = useLookupStore.getState().schoolYears as SchoolYearModel[];
      const activeSchoolYear =
        schoolYears.find((sy) => sy.status === "active") ?? schoolYears[0];

      const summaryYearIds = placementSchoolYearIds.length > 0
        ? [...placementSchoolYearIds].sort((a, b) => Number(b === activeSchoolYear?.id) - Number(a === activeSchoolYear?.id))
        : [activeSchoolYear?.id];

      const summaryBySid = new Map<string, GradeSummaryRow>();

      if (students.length > 0) {
        for (const yearId of summaryYearIds) {
          const yearSummary = await fetchTeacherGradingSummary(yearId);
          yearSummary.forEach((row, sid) => {
            if (!summaryBySid.has(sid)) summaryBySid.set(sid, row);
          });
        }
      }

      setRows(
        students.map((s) => {
          const m = toRosterMetrics(summaryBySid.get(s.id));
          return {
            id: s.id,
            type: "student_progress_data" as const,
            attributes: {
              first_name: s.first_name ?? "",
              last_name: s.last_name ?? "",
              email: s.email,
              user_name: s.user_name,
              status: m.status,
              progress: { completed: m.progressCompleted },
              // NOTE: "semsester_avg" is intentionally misspelled — every
              // consumer reads this exact key. Do not rename here.
              semsester_avg: m.semesterAvg,
              gpa: m.gpa,
              last_access: m.lastAccess,
            },
          };
        }),
      );
    } catch (err: unknown) {
      setError(getErrorMessage(err, "Failed to load students"));
    } finally {
      setLoading(false);
    }
  }, [profile?.id, classId]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  return { rows, loading, error, refetch: fetchStudents };
}

// ---------------------------------------------------------------------------
// Standalone reads — teacher StudentProfile + CourseProgress screens
//
// These back the teacher StudentProfile (grade-level / curriculum / school-
// year extras) and CourseProgress (per-subject cards) screens, which
// previously ran inline `supabase.from(...)` reads. Each preserves the EXACT
// query of the call site it replaced and returns the raw `{ data }` result.
// ---------------------------------------------------------------------------

/**
 * The student's most-recent grade-level row with the embedded grade-level /
 * curriculum / school-year names (StudentProfile extras). Profile fields live
 * on `students`; grade/curriculum/school-year linkage lives on
 * `student_grade_levels`. Ordered active-first then most-recently-updated,
 * limit 1. Returns the raw `{ data }` rows; the caller reads `[0]`.
 *
 * EMBED SHAPE: `curriculums` hangs off `student_grade_levels.curriculum_id`, NOT
 * off `grade_levels`. The previous nesting —
 * `grade_levels(grade_level, curriculums(curriculum_name))` — asked PostgREST
 * for a grade_levels→curriculums relationship that does not exist, so the whole
 * request 400'd and the teacher student drill-down showed no grade level, no
 * curriculum and no school year. Per-student curriculum is
 * `student_grade_levels.curriculum_id` (canonical; `subjects.curriculum_id` and
 * `grade_levels`-hung curriculum were both dropped), so all three embeds are
 * siblings at the top level.
 */
export const getStudentGradeLevelExtras = (studentId: string) =>
  supabase
    .from('student_grade_levels')
    .select(
      'active, updated_at, grade_levels(grade_level), curriculums(curriculum_name), school_years(name)',
    )
    .eq('student_id', studentId)
    .order('active', { ascending: false })
    .order('updated_at', { ascending: false })
    .limit(1);

/**
 * A class's subjects (class_subjects) with the embedded subject record, so the
 * CourseProgress page can render a card for EVERY subject (including ones with
 * no course yet). Returns the raw `{ data }` rows; the caller flattens
 * `r.subject`.
 */
export const listClassSubjects = (classId: string) =>
  supabase
    .from('class_subjects')
    .select('subject_id, subject:subjects(id, name, color, image)')
    .eq('class_id', classId);
